/**
 * /dashboard/accounting/returns — GST + TDS Returns hub (G_ACC7).
 *
 * Period selector (current month / quarter) + four tiles:
 *   1. GSTR-1   — outward supplies (monthly)
 *   2. GSTR-3B  — summary return (monthly)
 *   3. TDS 24Q  — salary deductions (quarterly, Apr-Jun=Q1)
 *   4. Form 16  — annual TDS certificate (annual)
 *
 * Each tile shows "Generate" (POST) → "Preview" + "Download CSV" once
 * generated. All backed by GET/POST /api/v1/accounting/tax-returns/*.
 *
 * Pattern: report-page shape mirrors
 * app.$tenantSlug.accounting.reports.profit-and-loss.tsx.
 */

import { useState } from "react";
import {
  createFileRoute,
  Link,
  useParams,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Download,
  FileText,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { formatPaise } from "@/lib/expense-format";
import { taxReturnCSVURL } from "@/lib/api/accounting";
import {
  useListTaxReturns,
  useGenerateTaxReturn,
} from "@/hooks/use-accounting";
import { toApiError } from "@/lib/api";
import {
  TAX_RETURN_CADENCE,
  TAX_RETURN_TYPE_LABELS,
  type TaxReturn,
  type TaxReturnType,
} from "@/types/accounting";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/returns",
)({
  component: ReturnsRoute,
});

// ---------------------------------------------------------------------------
// Period helpers
// ---------------------------------------------------------------------------

/** Returns current Indian FY year (Apr start). */
function currentFYYear(): number {
  const d = new Date();
  const m = d.getUTCMonth() + 1; // 1..12
  return m >= 4 ? d.getUTCFullYear() : d.getUTCFullYear() - 1;
}

/** Returns current month (1..12). */
function currentMonth(): number {
  return new Date().getUTCMonth() + 1;
}

/**
 * Returns current Indian FY quarter (1..4, 1=Apr-Jun).
 * Month 4-6 → Q1, 7-9 → Q2, 10-12 → Q3, 1-3 → Q4
 */
function currentFYQuarter(): number {
  const m = currentMonth();
  if (m >= 4 && m <= 6) return 1;
  if (m >= 7 && m <= 9) return 2;
  if (m >= 10 && m <= 12) return 3;
  return 4;
}

const MONTH_LABELS: Record<number, string> = {
  1: "January", 2: "February", 3: "March", 4: "April",
  5: "May", 6: "June", 7: "July", 8: "August",
  9: "September", 10: "October", 11: "November", 12: "December",
};

const QUARTER_LABELS: Record<number, string> = {
  1: "Q1 (Apr–Jun)", 2: "Q2 (Jul–Sep)", 3: "Q3 (Oct–Dec)", 4: "Q4 (Jan–Mar)",
};

function periodLabel(rt: TaxReturnType, year: number, index: number): string {
  const cadence = TAX_RETURN_CADENCE[rt];
  if (cadence === "monthly") return `${MONTH_LABELS[index]} ${year}`;
  if (cadence === "quarterly") return `${QUARTER_LABELS[index]} FY ${year}–${String(year + 1).slice(2)}`;
  return `FY ${year}–${String(year + 1).slice(2)}`;
}

function periodIndexForType(rt: TaxReturnType, month: number, quarter: number): number {
  const cadence = TAX_RETURN_CADENCE[rt];
  if (cadence === "monthly") return month;
  if (cadence === "quarterly") return quarter;
  return 1; // annual
}

// ---------------------------------------------------------------------------
// Year selector range
// ---------------------------------------------------------------------------

function yearRange(): number[] {
  const fy = currentFYYear();
  const years: number[] = [];
  for (let y = fy; y >= 2021; y--) {
    years.push(y);
  }
  return years;
}

// ---------------------------------------------------------------------------
// ReturnTile
// ---------------------------------------------------------------------------

interface ReturnTileProps {
  readonly returnType: TaxReturnType;
  readonly year: number;
  readonly periodIndex: number;
  readonly existing: TaxReturn | undefined;
  readonly onGenerate: (rt: TaxReturnType, year: number, idx: number) => void;
  readonly generating: boolean;
}

function ReturnTile({
  returnType,
  year,
  periodIndex,
  existing,
  onGenerate,
  generating,
}: ReturnTileProps) {
  const cadence = TAX_RETURN_CADENCE[returnType];
  const label = TAX_RETURN_TYPE_LABELS[returnType];
  const period = periodLabel(returnType, year, periodIndex);

  // Statutory anchor per return type
  const statutoryNote: Record<TaxReturnType, string> = {
    gstr_1: "CGST Act §37 — Outward supplies filed by 11th of following month.",
    gstr_3b: "CGST Act §39 — Monthly summary return + tax payment.",
    tds_24q: "IT Act §192 — Quarterly TDS on salary; NSDL Form 24Q.",
    tds_26q: "IT Act §194 — Quarterly TDS on non-salary payments; NSDL Form 26Q.",
    form_16: "IT Act §203 — Annual TDS certificate issued to employees.",
  };

  const cadenceBadge: Record<string, string> = {
    monthly: "Monthly",
    quarterly: "Quarterly",
    annual: "Annual",
  };

  return (
    <Card data-testid={`return-tile-${returnType}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
              <FileText className="h-4 w-4" />
            </span>
            <div>
              <CardTitle className="text-sm font-semibold">{label}</CardTitle>
              <CardDescription className="text-xs">{period}</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="shrink-0 text-xs capitalize">
            {cadenceBadge[cadence]}
          </Badge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{statutoryNote[returnType]}</p>
      </CardHeader>

      <CardContent className="space-y-3">
        {existing ? (
          <>
            <div className="grid grid-cols-2 gap-2 rounded-md bg-muted/40 px-3 py-2 text-xs">
              <div>
                <span className="text-muted-foreground">Documents</span>
                <p className="font-medium">{existing.document_count}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total amount</span>
                <p className="font-medium">{formatPaise(existing.total_minor)}</p>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Generated</span>
                <p className="font-medium">
                  {new Date(existing.generated_at).toLocaleString("en-IN", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                asChild
                variant="outline"
                size="sm"
                className="flex-1 gap-1.5"
                data-testid={`download-csv-${returnType}`}
              >
                <a
                  href={taxReturnCSVURL(existing.id)}
                  download
                  target="_blank"
                  rel="noreferrer"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download CSV
                </a>
              </Button>
            </div>
          </>
        ) : (
          <div className="rounded-md border border-dashed px-3 py-4 text-center">
            <p className="mb-3 text-sm text-muted-foreground">
              No return generated for this period yet.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={generating}
              onClick={() => onGenerate(returnType, year, periodIndex)}
              data-testid={`generate-${returnType}`}
            >
              {generating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Generate
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main route
// ---------------------------------------------------------------------------

const RETURN_TYPES: TaxReturnType[] = ["gstr_1", "gstr_3b", "tds_24q", "form_16"];

function ReturnsRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };

  const [year, setYear] = useState<number>(currentFYYear);
  const [month, setMonth] = useState<number>(currentMonth);
  const [quarter, setQuarter] = useState<number>(currentFYQuarter);

  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generatingType, setGeneratingType] = useState<TaxReturnType | null>(null);

  const listQuery = useListTaxReturns({ year });
  const items = listQuery.data?.items ?? [];
  const loading = listQuery.isPending;
  const listError = listQuery.isError
    ? toApiError(listQuery.error).message
    : null;

  const generateMutation = useGenerateTaxReturn();

  /** Find an already-generated return for the given (type, year, index). */
  function findExisting(rt: TaxReturnType): TaxReturn | undefined {
    const idx = periodIndexForType(rt, month, quarter);
    return items.find(
      (r) =>
        r.return_type === rt &&
        r.period_year === year &&
        r.period_index === idx,
    );
  }

  async function handleGenerate(
    rt: TaxReturnType,
    y: number,
    idx: number,
  ) {
    setGenerateError(null);
    setGeneratingType(rt);
    try {
      await generateMutation.mutateAsync({
        return_type: rt,
        period_year: y,
        period_index: idx,
      });
    } catch (err) {
      setGenerateError(toApiError(err).message);
    } finally {
      setGeneratingType(null);
    }
  }

  return (
    <AccountingModuleShell
      title="Tax Returns"
      description="GST and TDS returns — generate snapshots and download filing CSVs."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link
            to="/app/$tenantSlug/accounting/reports"
            params={{ tenantSlug }}
          >
            <ArrowLeft className="h-4 w-4" />
            Reports
          </Link>
        </Button>
      }
    >
      {/* Period selector */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-3">
            {/* FY year */}
            <div className="grid gap-1.5">
              <label className="text-sm font-medium" htmlFor="returns-year">
                FY Year
              </label>
              <Select
                value={String(year)}
                onValueChange={(v) => setYear(Number(v))}
              >
                <SelectTrigger
                  id="returns-year"
                  className="w-36"
                  data-testid="returns-year-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearRange().map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      FY {y}–{String(y + 1).slice(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Month (for monthly returns) */}
            <div className="grid gap-1.5">
              <label className="text-sm font-medium" htmlFor="returns-month">
                Month <span className="text-muted-foreground">(GSTR)</span>
              </label>
              <Select
                value={String(month)}
                onValueChange={(v) => setMonth(Number(v))}
              >
                <SelectTrigger
                  id="returns-month"
                  className="w-44"
                  data-testid="returns-month-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <SelectItem key={m} value={String(m)}>
                      {MONTH_LABELS[m]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quarter (for quarterly returns) */}
            <div className="grid gap-1.5">
              <label className="text-sm font-medium" htmlFor="returns-quarter">
                Quarter <span className="text-muted-foreground">(TDS)</span>
              </label>
              <Select
                value={String(quarter)}
                onValueChange={(v) => setQuarter(Number(v))}
              >
                <SelectTrigger
                  id="returns-quarter"
                  className="w-44"
                  data-testid="returns-quarter-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4].map((q) => (
                    <SelectItem key={q} value={String(q)}>
                      {QUARTER_LABELS[q]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Errors */}
      {(listError ?? generateError) && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{listError ?? generateError}</AlertDescription>
        </Alert>
      )}

      {/* Return tiles */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-52" />
            ))
          : RETURN_TYPES.map((rt) => {
              const idx = periodIndexForType(rt, month, quarter);
              const existing = findExisting(rt);
              return (
                <ReturnTile
                  key={rt}
                  returnType={rt}
                  year={year}
                  periodIndex={idx}
                  existing={existing}
                  onGenerate={handleGenerate}
                  generating={generatingType === rt}
                />
              );
            })}
      </div>
    </AccountingModuleShell>
  );
}
